import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, DollarSign, AlertCircle, X, Gift, Phone, Percent, Video } from 'lucide-react';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

const SubscriptionManagement = () => {
    const [loading, setLoading] = useState(true);
    const [details, setDetails] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelStep, setCancelStep] = useState(1);
    const [cancelData, setCancelData] = useState({
        reason: '',
        feedback: '',
        wants_founder_call: false,
        wants_lifetime_discount: false,
        wants_free_month_training: false
    });
    const [cancelling, setCancelling] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        loadSubscriptionDetails();
    }, []);

    const loadSubscriptionDetails = async () => {
        try {
            const response = await authService.authenticatedFetch(`${API_BASE}/api/subscription/details`);
            const data = await response.json();
            setDetails(data);
        } catch (error) {
            console.error('Failed to load subscription details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (cancelStep === 1 && !cancelData.reason) {
            setMessage('Please select a reason');
            return;
        }

        if (cancelStep === 1) {
            setCancelStep(2);
            setMessage('');
            return;
        }

        if (cancelStep === 2) {
            setCancelStep(3);
            setMessage('');
            return;
        }

        // Step 3 - Final cancellation or retention offer
        setCancelling(true);
        setMessage('');

        try {
            const response = await authService.authenticatedFetch(`${API_BASE}/api/subscription/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cancelData)
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.message);
                if (data.retention_offer_accepted) {
                    setTimeout(() => {
                        setShowCancelModal(false);
                        setCancelStep(1);
                        setCancelData({ 
                            reason: '', 
                            feedback: '', 
                            wants_founder_call: false, 
                            wants_lifetime_discount: false, 
                            wants_free_month_training: false 
                        });
                    }, 4500);
                } else {
                    setTimeout(() => {
                        setShowCancelModal(false);
                        setCancelStep(1);
                        setCancelData({ 
                            reason: '', 
                            feedback: '', 
                            wants_founder_call: false, 
                            wants_lifetime_discount: false, 
                            wants_free_month_training: false 
                        });
                        loadSubscriptionDetails();
                    }, 4500);
                }
            } else {
                setMessage(data.error || 'Failed to cancel subscription');
            }
        } catch (error) {
            setMessage('Failed to cancel subscription');
        } finally {
            setCancelling(false);
        }
    };

    const resetModal = () => {
        setShowCancelModal(false);
        setCancelStep(1);
        setCancelData({ 
            reason: '', 
            feedback: '', 
            wants_founder_call: false, 
            wants_lifetime_discount: false, 
            wants_free_month_training: false 
        });
        setMessage('');
    };

    const reasons = [
        'Too expensive',
        'Not using it enough',
        'Missing features I need',
        'Found a better alternative',
        'Technical issues',
        'Other'
    ];

    const hasSelectedOffer = cancelData.wants_founder_call || cancelData.wants_lifetime_discount || cancelData.wants_free_month_training;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 mb-6">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    if (!details?.subscription_active) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="text-teal-600" size={24} />
                    <h2 className="text-2xl font-bold text-gray-800">Subscription</h2>
                </div>
                <p className="text-gray-600 mb-4">No active subscription</p>
                <a
                    href="/pricing"
                    className="inline-block bg-gradient-to-r from-teal-500 to-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:opacity-90"
                >
                    View Plans
                </a>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-6">
                    <CreditCard className="text-teal-600" size={24} />
                    <h2 className="text-2xl font-bold text-gray-800">Subscription Management</h2>
                </div>

                <div className="space-y-6">
                    {/* Trial Status Alert */}
                    {details.is_trial && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                                <div>
                                    <p className="font-semibold text-yellow-900">Trial Period Active</p>
                                    <p className="text-sm text-yellow-800">
                                        Your trial expires on {details.trial_expires
                                            ? new Date(details.trial_expires).toLocaleDateString()
                                            : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Canceled Status Alert */}
                    {details.is_canceled && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                                <div>
                                    <p className="font-semibold text-red-900">Subscription Canceled</p>
                                    <p className="text-sm text-red-800">
                                        You'll have access until {details.subscription_expires
                                            ? new Date(details.subscription_expires).toLocaleDateString()
                                            : 'the end of your billing period'}.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Current Plan */}
                    <div className="border-b pb-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Current Plan</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-600">Plan</p>
                                <p className="text-lg font-bold text-teal-600">{details.subscription_plan}</p>
                            </div>
                            {details.billing_type && (
                                <div>
                                    <p className="text-sm text-gray-600">Billing</p>
                                    <p className="text-lg font-semibold text-gray-800">{details.billing_type}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Billing Information */}
                    {details.order_details && (
                        <div className="border-b pb-4">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Billing Information</h3>
                            <div className="space-y-3">
                                {details.order_details.next_billing_date && (
                                    <div className="flex items-center gap-2">
                                        <Calendar size={18} className="text-gray-500" />
                                        <span className="text-sm text-gray-600">Next billing date:</span>
                                        <span className="text-sm font-semibold text-gray-800">
                                            {new Date(details.order_details.next_billing_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                                {details.order_details.total_amount && (
                                    <div className="flex items-center gap-2">
                                        <DollarSign size={18} className="text-gray-500" />
                                        <span className="text-sm text-gray-600">Amount:</span>
                                        <span className="text-sm font-semibold text-gray-800">
                                            {details.order_details.currency} {details.order_details.total_amount}
                                        </span>
                                    </div>
                                )}
                                {details.order_details.order_date && (
                                    <div className="flex items-center gap-2">
                                        <Calendar size={18} className="text-gray-500" />
                                        <span className="text-sm text-gray-600">Started:</span>
                                        <span className="text-sm font-semibold text-gray-800">
                                            {new Date(details.order_details.order_date).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                                {details.order_details.payment_method && (
                                    <div className="flex items-center gap-2">
                                        <CreditCard size={18} className="text-gray-500" />
                                        <span className="text-sm text-gray-600">Payment method:</span>
                                        <span className="text-sm font-semibold text-gray-800">
                                            {details.order_details.payment_method}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <a
                            href="/pricing"
                            className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 text-center"
                        >
                            Upgrade Plan
                        </a>
                        {details.order_details?.can_cancel && !details.is_canceled && (
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className="flex-1 bg-red-50 text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-100 border border-red-200 transition"
                            >
                                Cancel Subscription
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Cancellation Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        {/* Header */}
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-xl font-bold text-gray-800">
                                {cancelStep === 1 && 'Before you go...'}
                                {cancelStep === 2 && 'Help us improve'}
                                {cancelStep === 3 && 'Special offers just for you'}
                            </h3>
                            <button
                                onClick={resetModal}
                                className="text-gray-400 hover:text-gray-600 transition"
                                disabled={cancelling}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {cancelStep === 1 && (
                                <div className="space-y-4">
                                    <p className="text-gray-600 mb-4">We're sorry to see you go. Could you tell us why you're cancelling?</p>
                                    <div className="space-y-2">
                                        {reasons.map((reason) => (
                                            <label
                                                key={reason}
                                                className={`block p-4 border-2 rounded-lg cursor-pointer transition ${cancelData.reason === reason
                                                        ? 'border-teal-500 bg-teal-50'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="reason"
                                                    value={reason}
                                                    checked={cancelData.reason === reason}
                                                    onChange={(e) => setCancelData({ ...cancelData, reason: e.target.value })}
                                                    className="mr-3"
                                                />
                                                <span className="text-gray-800">{reason}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cancelStep === 2 && (
                                <div className="space-y-4">
                                    <p className="text-gray-600 mb-2">Is there anything else you'd like to share? (Optional)</p>
                                    <textarea
                                        value={cancelData.feedback}
                                        onChange={(e) => setCancelData({ ...cancelData, feedback: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                                        rows="5"
                                        placeholder="Your feedback helps us improve..."
                                    />
                                </div>
                            )}

                            {cancelStep === 3 && (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-4 rounded-lg border border-teal-200 mb-4">
                                        <p className="font-semibold text-teal-900 mb-2">We'd love to keep you!</p>
                                        <p className="text-sm text-teal-800">Select one or more exclusive offers below:</p>
                                    </div>

                                    <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition ${
                                        cancelData.wants_founder_call ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                                    }`}>
                                        <input
                                            type="checkbox"
                                            checked={cancelData.wants_founder_call}
                                            onChange={(e) => setCancelData({
                                                ...cancelData,
                                                wants_founder_call: e.target.checked
                                            })}
                                            className="mt-1 mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Phone size={20} className="text-teal-600" />
                                                <p className="font-semibold text-gray-800">1-on-1 Call with Founder/CEO</p>
                                            </div>
                                            <p className="text-sm text-gray-600">Get direct access to our founder for personalized guidance and insights</p>
                                        </div>
                                    </label>

                                    <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition ${
                                        cancelData.wants_lifetime_discount ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                                    }`}>
                                        <input
                                            type="checkbox"
                                            checked={cancelData.wants_lifetime_discount}
                                            onChange={(e) => setCancelData({
                                                ...cancelData,
                                                wants_lifetime_discount: e.target.checked
                                            })}
                                            className="mt-1 mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Percent size={20} className="text-teal-600" />
                                                <p className="font-semibold text-gray-800">10% OFF Lifetime</p>
                                            </div>
                                            <p className="text-sm text-gray-600">Save 10% on your subscription for as long as you stay with us</p>
                                        </div>
                                    </label>

                                    <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition ${
                                        cancelData.wants_free_month_training ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                                    }`}>
                                        <input
                                            type="checkbox"
                                            checked={cancelData.wants_free_month_training}
                                            onChange={(e) => setCancelData({
                                                ...cancelData,
                                                wants_free_month_training: e.target.checked
                                            })}
                                            className="mt-1 mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Video size={20} className="text-teal-600" />
                                                <p className="font-semibold text-gray-800">Free Month + Weekly Training Calls</p>
                                            </div>
                                            <p className="text-sm text-gray-600">Get a free month with personalized weekly training sessions to help you succeed</p>
                                        </div>
                                    </label>

                                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 mt-4">
                                        <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-amber-800">
                                            {hasSelectedOffer 
                                                ? 'Our team will reach out within 24 hours to set up your selected offer(s).'
                                                : 'If you don\'t select an offer, your subscription will be cancelled and you\'ll retain access until the end of your billing period.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            )}

                            {message && (
                                <div className={`mt-4 p-3 rounded-lg flex items-start gap-2 ${message.includes('success') || message.includes('Thank you') || message.includes('successfully')
                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                    }`}>
                                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                                    <p className="text-sm">{message}</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => {
                                    if (cancelStep > 1) {
                                        setCancelStep(cancelStep - 1);
                                        setMessage('');
                                    } else {
                                        resetModal();
                                    }
                                }}
                                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition disabled:opacity-50"
                                disabled={cancelling}
                            >
                                {cancelStep === 1 ? 'Keep Subscription' : 'Back'}
                            </button>
                            <button
                                onClick={handleCancelSubscription}
                                disabled={cancelling || (cancelStep === 1 && !cancelData.reason)}
                                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:opacity-50 transition"
                            >
                                {cancelling ? 'Processing...' : (
                                    cancelStep === 3 ? (
                                        hasSelectedOffer ? 'Accept Offer(s)' : 'Confirm Cancellation'
                                    ) : 'Continue'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SubscriptionManagement;